import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dropdown,
  DropdownMenu,
  Button,
  Icon,
  MenuSection,
  ConfirmationModal,
} from '@folio/stripes-components';
import { FormattedMessage } from 'react-intl';
import { IfPermission, useStripes } from '@folio/stripes-core';

const DashboardsActionsMenu = ({ currentDashboard, onCreateDashboard, onEditDashboard, onDeleteDashboard }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const stripes = useStripes();

  const hasAnyAction =
    stripes.hasPerm('ui-reports.dashboards.create') ||
    stripes.hasPerm('ui-reports.dashboards.edit') ||
    stripes.hasPerm('ui-reports.dashboards.delete') ||
    stripes.hasPerm('ui-reports.widgets.create');

  if (!hasAnyAction) return null;

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  const getDropdownContent = () => (
    <MenuSection>
      <IfPermission perm="ui-reports.widgets.create">
        {currentDashboard && (
          <Button
            marginBottom0
            id="dashboards-actions-menu-create-widget-btn"
            buttonStyle="dropdownItem"
            to={{ pathname: `/reports/dashboards/${currentDashboard.id}/widgets/create` }}
            onMouseDown={(e) => e.preventDefault()}
          >
            <Icon icon="plus-sign">
              <FormattedMessage id="ui-reports.dashboards.widgets.addNewWidget.btn" />
            </Icon>
          </Button>
        )}
      </IfPermission>

      <IfPermission perm="ui-reports.dashboards.create">
        <Button
          marginBottom0
          id="dashboards-actions-menu-create-dashboard-btn"
          buttonStyle="dropdownItem"
          onClick={() => { toggleDropdown(); onCreateDashboard(); }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <Icon icon="plus-sign">
            <FormattedMessage id="ui-reports.dashboard.actions.create" />
          </Icon>
        </Button>
      </IfPermission>

      <IfPermission perm="ui-reports.dashboards.edit">
        {currentDashboard && (
          <Button
            marginBottom0
            id="dashboards-actions-menu-edit-dashboard-btn"
            buttonStyle="dropdownItem"
            onClick={() => { toggleDropdown(); onEditDashboard(currentDashboard); }}
            onMouseDown={(e) => e.preventDefault()}
          >
            <Icon icon="edit">
              <FormattedMessage id="ui-reports.dashboard.actions.edit" />
            </Icon>
          </Button>
        )}
      </IfPermission>

      <IfPermission perm="ui-reports.dashboards.delete">
        {currentDashboard && (
          <Button
            marginBottom0
            id="dashboards-actions-menu-delete-dashboard-btn"
            buttonStyle="dropdownItem"
            onClick={() => { toggleDropdown(); setShowDeleteConfirm(true); }}
            onMouseDown={(e) => e.preventDefault()}
          >
            <Icon icon="trash">
              <FormattedMessage id="ui-reports.dashboard.actions.delete" />
            </Icon>
          </Button>
        )}
      </IfPermission>
    </MenuSection>
  );

  const renderTrigger = ({ getTriggerProps }) => (
    <FormattedMessage id="ui-reports.dashboardsActionsMenu" defaultMessage="Dashboards Actions Menu">
      {(label) => (
        <div style={{ paddingRight: '0.25em', paddingLeft: '0.25em' }}>
          <Button
            data-test-pane-header-actions-button
            buttonStyle="primary"
            marginBottom0
            ariaLabel={label}
            type="button"
            {...getTriggerProps()}
          >
            <Icon icon="ellipsis" size="large" />
          </Button>
        </div>
      )}
    </FormattedMessage>
  );

  return (
    <>
      <Dropdown
        id="dashboards-actionsMenu-dropdown"
        renderTrigger={renderTrigger}
        renderMenu={({ open }) => <DropdownMenu open={open}>{getDropdownContent()}</DropdownMenu>}
        open={dropdownOpen}
        onToggle={toggleDropdown}
        placement="bottom-end"
        relativePosition
        focusHandlers={{ open: () => null }}
      />

      <ConfirmationModal
        open={showDeleteConfirm}
        heading={<FormattedMessage id="ui-reports.dashboard.delete.confirm.heading" />}
        message={<FormattedMessage id="ui-reports.dashboard.delete.confirm.message" />}
        onConfirm={() => { setShowDeleteConfirm(false); onDeleteDashboard(currentDashboard.id); }}
        onCancel={() => setShowDeleteConfirm(false)}
        confirmLabel={<FormattedMessage id="ui-reports.button.delete" />}
      />
    </>
  );
};

DashboardsActionsMenu.propTypes = {
  currentDashboard: PropTypes.object,
  onCreateDashboard: PropTypes.func.isRequired,
  onEditDashboard: PropTypes.func.isRequired,
  onDeleteDashboard: PropTypes.func.isRequired,
};

export default DashboardsActionsMenu;
